import React from 'react';
import { Plus, Trash2, Pencil, ShoppingCart, X, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { formatDate } from '../../lib/dateUtils';
import { EGG_LABELS, EGG_COLORS, normalizeTipoKey } from './constants';

/**
 * Tab: registro de compras de huevos a proveedores externos.
 * Incluye formulario de alta/edición y tabla histórica.
 */
export const ComprasTab = ({
  compras,
  loadingCompras,
  comprasError,
  compraForm,
  setCompraField,
  editingCompraId,
  savingCompra,
  onSaveCompra,
  onResetCompra,
  onEditCompra,
  onDeleteCompra,
  filtroCompraTipo,
  setFiltroCompraTipo,
}) => (
  <div className="space-y-6">
    {/* Aviso si la tabla no existe */}
    {comprasError && (
      <Card className="border-amber-300 bg-amber-50/50 dark:bg-amber-950/20">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-semibold text-amber-900 dark:text-amber-200 mb-1">
              Configuración pendiente
            </p>
            <p className="text-amber-800 dark:text-amber-300 text-xs">{comprasError}</p>
          </div>
        </CardContent>
      </Card>
    )}

    {/* Formulario de Compra */}
    <Card data-testid="form-compra-huevo">
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <ShoppingCart className="w-5 h-5" />
            {editingCompraId ? 'Editar Compra de Huevos' : 'Registrar Compra de Huevos'}
          </CardTitle>
          {editingCompraId && (
            <Button variant="ghost" size="sm" onClick={onResetCompra} data-testid="btn-cancelar-edit-compra">
              <X className="w-4 h-4 mr-1" /> Cancelar edición
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Registra entradas de huevos comprados a proveedores externos. Suma directamente al inventario general.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <Label htmlFor="compra-fecha">Fecha *</Label>
            <Input
              id="compra-fecha"
              type="date"
              value={compraForm.fecha}
              onChange={(e) => setCompraField('fecha', e.target.value)}
              className="h-11 mt-1.5"
              data-testid="input-compra-fecha"
            />
          </div>
          <div>
            <Label htmlFor="compra-proveedor">Proveedor *</Label>
            <Input
              id="compra-proveedor"
              type="text"
              placeholder="Ej. Granja San José"
              value={compraForm.proveedor}
              onChange={(e) => setCompraField('proveedor', e.target.value)}
              className="h-11 mt-1.5"
              maxLength={120}
              data-testid="input-compra-proveedor"
            />
          </div>
          <div>
            <Label>Tipo de Huevo *</Label>
            <Select
              value={compraForm.tipo_huevo}
              onValueChange={(v) => setCompraField('tipo_huevo', v)}
              data-testid="select-compra-tipo"
            >
              <SelectTrigger className="h-11 mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(EGG_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="compra-cantidad">Cantidad *</Label>
            <Input
              id="compra-cantidad"
              type="number"
              inputMode="numeric"
              min="1"
              placeholder="0"
              value={compraForm.cantidad}
              onChange={(e) => setCompraField('cantidad', e.target.value)}
              className="h-11 mt-1.5 tabular-nums"
              data-testid="input-compra-cantidad"
            />
            <p className="text-[10px] text-muted-foreground mt-0.5">unidades</p>
          </div>
        </div>

        <div className="mt-4">
          <Label htmlFor="compra-obs">Observaciones</Label>
          <textarea
            id="compra-obs"
            rows={2}
            value={compraForm.observaciones}
            onChange={(e) => setCompraField('observaciones', e.target.value)}
            placeholder="Notas, número de remisión, condiciones de pago, etc. (opcional)"
            className="w-full mt-1.5 px-3 py-2 text-sm border rounded-md bg-background min-h-[60px]"
            data-testid="input-compra-observaciones"
          />
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <Button onClick={onSaveCompra} disabled={savingCompra} data-testid="btn-guardar-compra">
            <Plus className="w-4 h-4 mr-2" />
            {savingCompra
              ? 'Guardando...'
              : (editingCompraId ? 'Actualizar Compra' : 'Registrar Compra')}
          </Button>
          {editingCompraId && (
            <Button variant="outline" onClick={onResetCompra} disabled={savingCompra}>
              Cancelar
            </Button>
          )}
        </div>
      </CardContent>
    </Card>

    {/* Historial de Compras */}
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
          <ShoppingCart className="w-5 h-5" /> Historial de Compras ({compras.length})
        </CardTitle>
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={filtroCompraTipo} onValueChange={setFiltroCompraTipo}>
            <SelectTrigger className="w-[160px] h-9" data-testid="filtro-compra-tipo">
              <SelectValue placeholder="Tipo huevo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              {Object.entries(EGG_LABELS).map(([key, label]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFiltroCompraTipo('all')}
            data-testid="btn-limpiar-filtro-compra"
          >
            Limpiar
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loadingCompras ? (
          <div className="text-center py-8 text-muted-foreground">Cargando compras...</div>
        ) : compras.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No hay compras registradas</p>
            <p className="text-xs mt-1">Registra tu primera compra externa para llevar el control</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="tabla-compras">
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Proveedor</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="text-right">Cantidad</TableHead>
                  <TableHead>Observaciones</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {compras.map((c) => {
                  const cId = c.id || c._id;
                  const tipoKey = normalizeTipoKey(c.tipo_huevo);
                  const isEditing = editingCompraId === cId;
                  return (
                    <TableRow
                      key={cId}
                      className={`hover:bg-accent/60 transition-colors ${isEditing ? 'bg-accent/40' : ''}`}
                    >
                      <TableCell>{formatDate(c.fecha)}</TableCell>
                      <TableCell className="font-medium">{c.proveedor}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-xs ${EGG_COLORS[tipoKey] || ''}`}>
                          {EGG_LABELS[tipoKey] || c.tipo_huevo}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-semibold text-green-700">
                        +{Number(c.cantidad || 0).toLocaleString('en-US')}
                      </TableCell>
                      <TableCell
                        className="text-muted-foreground text-sm max-w-[250px] truncate"
                        title={c.observaciones || ''}
                      >
                        {c.observaciones || '—'}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onEditCompra(c)}
                          title="Editar"
                          data-testid={`btn-edit-compra-${cId}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onDeleteCompra(c)}
                          title="Eliminar"
                          data-testid={`btn-delete-compra-${cId}`}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  </div>
);
